import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import swal from "sweetalert";
import { Link } from "react-router-dom";
import {
  FaBook,
  FaPlus,
  FaSearch,
  FaArrowRight,
  FaWallet,
  FaMoneyBillWave,
  FaReceipt,
  FaFileInvoiceDollar,
  FaChartPie,
  FaInfoCircle,
  FaChevronDown,
  FaChevronUp,
  FaThLarge,
  FaList,
  FaCheck,
  FaQuestionCircle,
  FaTag,
  FaShoppingBag,
  FaTruck,
  FaUndoAlt,
  FaExchangeAlt,
  FaSync,
} from "react-icons/fa";

// Plain-English category definitions
const CATEGORIES = {
  ASSET: {
    key: "ASSET",
    label: "Assets",
    simpleTitle: "Assets — What You Own & Cash",
    description: "Orders waiting for payment, cash collected on delivery, and the bank",
    color: "#2563eb",
    bgColor: "#eff6ff",
    borderColor: "#bfdbfe",
    badgeClass: "bg-primary text-white",
    icon: FaWallet,
    normalBalance: "DEBIT",
    defaultCodePrefix: "1",
  },
  REVENUE: {
    key: "REVENUE",
    label: "Income & Revenue",
    simpleTitle: "Income — Money You Earn",
    description: "Product sales revenue, delivery fees collected, and store earnings",
    color: "#16a34a",
    bgColor: "#ecfdf5",
    borderColor: "#a7f3d0",
    badgeClass: "bg-success text-white",
    icon: FaMoneyBillWave,
    normalBalance: "CREDIT",
    defaultCodePrefix: "4",
  },
  EXPENSE: {
    key: "EXPENSE",
    label: "Expenses & Costs",
    simpleTitle: "Expenses — Money Spent",
    description: "Cost of goods sold, shipping partner fees, payment gateway commissions, and bills",
    color: "#d97706",
    bgColor: "#fffbeb",
    borderColor: "#fde68a",
    badgeClass: "bg-warning text-dark",
    icon: FaReceipt,
    normalBalance: "DEBIT",
    defaultCodePrefix: "6",
  },
  LIABILITY: {
    key: "LIABILITY",
    label: "Liabilities",
    simpleTitle: "Liabilities — What You Owe",
    description: "Amounts owed to suppliers, sales tax & VAT payable to authorities",
    color: "#e11d48",
    bgColor: "#fff1f2",
    borderColor: "#fecdd3",
    badgeClass: "bg-danger text-white",
    icon: FaFileInvoiceDollar,
    normalBalance: "CREDIT",
    defaultCodePrefix: "2",
  },
  EQUITY: {
    key: "EQUITY",
    label: "Equity & Capital",
    simpleTitle: "Equity — Owner's Capital",
    description: "Owner capital investments and accumulated retained profits",
    color: "#7c3aed",
    bgColor: "#f5f3ff",
    borderColor: "#ddd6fe",
    badgeClass: "bg-dark text-white",
    icon: FaChartPie,
    normalBalance: "CREDIT",
    defaultCodePrefix: "3",
  },
};

// Helper: auto-suggest next code
const calculateNextCode = (type, existingAccounts) => {
  const prefix = CATEGORIES[type]?.defaultCodePrefix || "1";
  const matchingCodes = existingAccounts
    .filter((a) => a.account_type === type && /^\d+$/.test(a.account_code))
    .map((a) => parseInt(a.account_code, 10))
    .filter((num) => num.toString().startsWith(prefix));

  if (matchingCodes.length === 0) {
    return prefix + "100";
  }
  const maxCode = Math.max(...matchingCodes);
  return (maxCode + 10).toString();
};

export default function ChartOfAccountView() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grouped"); // 'grouped' or 'table'
  const [showGuide, setShowGuide] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAdvancedInModal, setShowAdvancedInModal] = useState(false);
  const [simpleMode, setSimpleMode] = useState(true);

  const [formData, setFormData] = useState({
    account_code: "",
    account_name: "",
    account_type: "REVENUE",
    normal_balance: "CREDIT",
    parent_id: "",
    description: "",
  });

  const fetchAccounts = () => {
    setLoading(true);
    axios
      .get("/api/admin/accounting/chart-of-accounts")
      .then((res) => {
        if (res.data.status === 200) {
          setAccounts(res.data.accounts || []);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    document.title = "Chart of Accounts — Admin";
    fetchAccounts();
  }, []);

  // Simple Mode Core Accounts: Sale, Refund, Courier, Discount, Cash
  const SIMPLE_ACCOUNT_CODES = ["4100", "4400", "4200", "4300", "1110"];
  const saleAccount = useMemo(() => accounts.find((a) => a.account_code === "4100"), [accounts]);
  const refundAccount = useMemo(() => accounts.find((a) => a.account_code === "4400"), [accounts]);
  const courierAccount = useMemo(() => accounts.find((a) => a.account_code === "4200"), [accounts]);
  const discountAccount = useMemo(() => accounts.find((a) => a.account_code === "4300"), [accounts]);
  const cashAccount = useMemo(() => accounts.find((a) => a.account_code === "1110"), [accounts]);

  const [syncingSales, setSyncingSales] = useState(false);

  const handleSyncSales = () => {
    setSyncingSales(true);
    axios
      .post("/api/admin/accounting/sync-sales")
      .then((res) => {
        setSyncingSales(false);
        if (res.data.status === 200) {
          swal("Sync Complete", res.data.message || "Completed orders synced to Sales and cancelled orders excluded.", "success");
          fetchAccounts();
        } else {
          swal("Sync Status", res.data.message || "Sync finished.", "info");
        }
      })
      .catch((err) => {
        setSyncingSales(false);
        swal("Sync Notice", err.response?.data?.message || "Sync completed.", "info");
      });
  };

  const netTotalRevenue = useMemo(() => {
    const saleBal = Number(saleAccount?.balance || 0);
    const courierBal = Number(courierAccount?.balance || 0);
    const refBal = Number(refundAccount?.balance || 0);
    const discBal = Number(discountAccount?.balance || 0);
    return Math.max(0, saleBal + courierBal - refBal - discBal);
  }, [saleAccount, courierAccount, refundAccount, discountAccount]);

  // When opening modal or changing category, auto-suggest code & normal balance
  const handleOpenModal = (preselectedType = "REVENUE") => {
    const nextCode = calculateNextCode(preselectedType, accounts);
    const norm = CATEGORIES[preselectedType]?.normalBalance || "CREDIT";
    setFormData({
      account_code: nextCode,
      account_name: "",
      account_type: preselectedType,
      normal_balance: norm,
      parent_id: "",
      description: "",
    });
    setShowAdvancedInModal(false);
    setOpenModal(true);
  };

  const handleTypeSelectInModal = (type) => {
    const nextCode = calculateNextCode(type, accounts);
    const norm = CATEGORIES[type]?.normalBalance || "DEBIT";
    setFormData((prev) => ({
      ...prev,
      account_type: type,
      account_code: nextCode,
      normal_balance: norm,
    }));
  };

  const handleSaveAccount = (e) => {
    e.preventDefault();
    if (!formData.account_name.trim()) {
      swal("Missing Information", "Please enter an account name.", "warning");
      return;
    }
    setSaving(true);

    axios
      .post("/api/admin/accounting/accounts", formData)
      .then((res) => {
        setSaving(false);
        if (res.data.status === 200) {
          swal("Account Created", res.data.message || "Account successfully created", "success", {
            buttons: false,
            timer: 1500,
          });
          setOpenModal(false);
          fetchAccounts();
        } else {
          swal("Error", res.data.message || "Could not save account", "error");
        }
      })
      .catch((err) => {
        setSaving(false);
        const errMsg =
          err.response?.data?.errors?.account_code?.[0] ||
          err.response?.data?.message ||
          "Failed to save account";
        swal("Error", errMsg, "error");
      });
  };

  // Filter accounts (hides costing and complex accounts in Simple Mode)
  const filteredAccounts = useMemo(() => {
    return accounts.filter((acc) => {
      if (simpleMode && !SIMPLE_ACCOUNT_CODES.includes(acc.account_code)) {
        return false;
      }
      const matchesType = filterType === "ALL" || acc.account_type === filterType;
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        acc.account_code.toLowerCase().includes(q) ||
        acc.account_name.toLowerCase().includes(q) ||
        (acc.description && acc.description.toLowerCase().includes(q));
      return matchesType && matchesSearch;
    });
  }, [accounts, filterType, searchQuery, simpleMode]);

  // Financial summary totals
  const summaryTotals = useMemo(() => {
    const totals = {
      ASSET: 0,
      REVENUE: 0,
      EXPENSE: 0,
      LIABILITY: 0,
      EQUITY: 0,
    };
    accounts.forEach((acc) => {
      const type = acc.account_type;
      const bal = Number(acc.balance || 0);
      if (totals[type] !== undefined) {
        totals[type] += bal;
      }
    });
    return totals;
  }, [accounts]);

  // Format currency
  const formatMoney = (val) => {
    return "Rs. " + Number(val || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Grouped list by category
  const groupedData = useMemo(() => {
    const groups = [];
    Object.keys(CATEGORIES).forEach((type) => {
      if (filterType !== "ALL" && filterType !== type) return;
      const items = filteredAccounts.filter((a) => a.account_type === type);
      if (items.length > 0 || (searchQuery === "" && filterType === type)) {
        const subtotal = items.reduce((sum, a) => sum + Number(a.balance || 0), 0);
        groups.push({
          category: CATEGORIES[type],
          items,
          subtotal,
        });
      }
    });
    return groups;
  }, [filteredAccounts, filterType, searchQuery]);

  return (
    <div>
      {/* Page Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <h4 className="fw-bold mb-0" style={{ color: "var(--admin-text-main)", letterSpacing: "-0.3px" }}>
              Chart of Accounts
            </h4>
            {simpleMode && (
              <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-2 py-1" style={{ fontSize: "11px" }}>
                Simple Mode Active
              </span>
            )}
          </div>
          <p className="text-muted mb-0" style={{ fontSize: "13.5px" }}>
            {simpleMode
              ? "Simple store accounts: Sale, Refund, Courier, Discount, and Cash settlement"
              : "Complete double-entry chart of accounts with all asset, revenue, and expense ledgers"}
          </p>
        </div>

        <div className="d-flex align-items-center flex-wrap gap-2">
          {/* Simple vs All Mode Switcher */}
          <div className="btn-group btn-group-sm rounded-pill p-1 bg-white border shadow-sm">
            <button
              type="button"
              className={`btn btn-sm rounded-pill px-3 py-1 fw-bold ${
                simpleMode ? "btn-primary text-white shadow-sm" : "text-muted border-0 bg-transparent"
              }`}
              style={{ fontSize: "12px" }}
              onClick={() => {
                setSimpleMode(true);
                setFilterType("ALL");
              }}
            >
              <FaShoppingBag size={11} className="me-1" /> Simple (Sale, Refund, Courier)
            </button>
            <button
              type="button"
              className={`btn btn-sm rounded-pill px-3 py-1 fw-bold ${
                !simpleMode ? "btn-dark text-white shadow-sm" : "text-muted border-0 bg-transparent"
              }`}
              style={{ fontSize: "12px" }}
              onClick={() => setSimpleMode(false)}
            >
              <FaBook size={11} className="me-1" /> All Accounts
            </button>
          </div>

          <button
            type="button"
            onClick={handleSyncSales}
            disabled={syncingSales}
            className="btn btn-sm btn-outline-success rounded-pill px-3 py-2 fw-semibold d-flex align-items-center gap-2 shadow-sm"
            style={{ fontSize: "13px" }}
            title="Auto-create Sales for delivered orders and strictly exclude cancelled orders from Sales Revenue"
          >
            <FaSync size={12} className={syncingSales ? "fa-spin" : ""} />
            {syncingSales ? "Syncing..." : "Sync & Reconcile Sales"}
          </button>

          <button
            type="button"
            onClick={() => setShowGuide(!showGuide)}
            className="btn btn-sm btn-outline-secondary rounded-pill px-3 py-2 d-flex align-items-center gap-2"
            style={{ fontSize: "13px" }}
          >
            <FaQuestionCircle size={13} />
            {showGuide ? "Hide Guide" : "How it works"}
          </button>
          <Link
            to="/admin/ledger"
            className="btn btn-sm btn-outline-dark rounded-pill px-3 py-2 fw-semibold d-flex align-items-center gap-2"
            style={{ fontSize: "13px" }}
          >
            <FaBook size={13} /> View Ledger
          </Link>
          <button
            type="button"
            onClick={() => handleOpenModal("REVENUE")}
            className="btn btn-sm btn-primary rounded-pill px-3 py-2 fw-semibold d-flex align-items-center gap-2"
            style={{ fontSize: "13px", background: "var(--admin-primary)", border: "none" }}
          >
            <FaPlus size={11} /> New Account
          </button>
        </div>
      </div>

      {/* Explainer / Guide Banner */}
      {showGuide && (
        <div
          className="admin-card p-3 mb-4 border-0"
          style={{
            backgroundColor: "#f0fdf4",
            borderLeft: "4px solid #16a34a",
            borderRadius: "10px",
          }}
        >
          <div className="d-flex align-items-start gap-3">
            <FaInfoCircle size={22} className="text-success mt-1 flex-shrink-0" />
            <div style={{ fontSize: "13px", color: "#166534" }}>
              <h6 className="fw-bold mb-1" style={{ color: "#14532d" }}>
                {simpleMode ? "Simple Store Accounting & Order Sales Overview" : "What is the Chart of Accounts?"}
              </h6>
              {simpleMode ? (
                <div>
                  <p className="mb-2">
                    Your bookkeeping is streamlined to track sales revenue, courier fees, and refunds cleanly without complex accounting jargon:
                  </p>
                  <div className="row g-2">
                    <div className="col-12 col-md-4">
                      <strong>🛍️ Sale Account (4100):</strong> Recorded from fulfilled customer orders. Cancelled orders are strictly excluded from sales revenue.
                    </div>
                    <div className="col-12 col-md-4">
                      <strong>🔄 Refund Account (4400):</strong> Records returned goods and order refunds issued.
                    </div>
                    <div className="col-12 col-md-4">
                      <strong>🚚 Courier Account (4200):</strong> Records shipping and delivery fees collected from customers.
                    </div>
                    <div className="col-12 col-md-4">
                      <strong>🏷️ Discount Account (4300):</strong> Tracks coupon codes and price reductions given.
                    </div>
                    <div className="col-12 col-md-4">
                      <strong>💵 Cash & Settlement (1110):</strong> Tracks COD receipts and online gateway deposits.
                    </div>
                    <div className="col-12 col-md-4">
                      <strong>⚡ Order Auto-Sale:</strong> Any order marked Delivered or Complete automatically generates a commercial Sale & journal entry.
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="mb-2">
                    Think of your Chart of Accounts as labeled financial buckets for your store:
                  </p>
                  <div className="row g-2">
                    <div className="col-12 col-md-6">
                      <strong>💼 Assets:</strong> What you have — cash on hand, bank account balance, courier collections, and warehouse stock.
                    </div>
                    <div className="col-12 col-md-6">
                      <strong>📈 Income / Revenue:</strong> Money earned — product sales and shipping charges collected from customers.
                    </div>
                    <div className="col-12 col-md-6">
                      <strong>💳 Expenses:</strong> Money spent — courier delivery fees, gateway transaction charges, wholesale product costs, and store bills.
                    </div>
                    <div className="col-12 col-md-6">
                      <strong>⏳ Liabilities:</strong> Money you owe — sales tax/VAT collected to pay to the government, and supplier bills.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Summary Stat Cards */}
      <div className="row g-3 mb-4">
        {simpleMode ? (
          <>
            {/* 1. Sale Account */}
            <div className="col-12 col-sm-6 col-lg-4 col-xl-2">
              <div className="admin-stat-card h-100 shadow-sm border-0" style={{ borderLeft: "4px solid #16a34a" }}>
                <div className="admin-stat-header">
                  <div>
                    <span className="badge bg-success bg-opacity-10 text-success fw-bold font-monospace px-2 py-0 mb-1" style={{ fontSize: "11px" }}>
                      4100
                    </span>
                    <div className="admin-stat-label fw-bold text-dark" style={{ fontSize: "13.5px" }}>Sale Account</div>
                  </div>
                  <div className="admin-stat-icon" style={{ background: "#ecfdf5", color: "#16a34a" }}>
                    <FaShoppingBag />
                  </div>
                </div>
                <div className="admin-stat-value text-success" style={{ fontSize: "20px" }}>
                  {formatMoney(saleAccount?.balance || 0)}
                </div>
                <div className="d-flex align-items-center justify-content-between mt-2 pt-2 border-top" style={{ fontSize: "11.5px" }}>
                  <span className="text-muted" title="Active fulfilled sales (Cancelled orders excluded)">Product sales (Active)</span>
                  <Link
                    to={saleAccount ? `/admin/ledger?accountId=${saleAccount.id}` : "/admin/ledger"}
                    className="text-primary fw-semibold text-decoration-none d-flex align-items-center gap-1"
                  >
                    Ledger <FaArrowRight size={10} />
                  </Link>
                </div>
              </div>
            </div>

            {/* 2. Refund Account */}
            <div className="col-12 col-sm-6 col-lg-4 col-xl-2">
              <div className="admin-stat-card h-100 shadow-sm border-0" style={{ borderLeft: "4px solid #e11d48" }}>
                <div className="admin-stat-header">
                  <div>
                    <span className="badge bg-danger bg-opacity-10 text-danger fw-bold font-monospace px-2 py-0 mb-1" style={{ fontSize: "11px" }}>
                      4400
                    </span>
                    <div className="admin-stat-label fw-bold text-dark" style={{ fontSize: "13.5px" }}>Refund Account</div>
                  </div>
                  <div className="admin-stat-icon" style={{ background: "#fff1f2", color: "#e11d48" }}>
                    <FaUndoAlt />
                  </div>
                </div>
                <div className="admin-stat-value text-danger" style={{ fontSize: "20px" }}>
                  {formatMoney(refundAccount?.balance || 0)}
                </div>
                <div className="d-flex align-items-center justify-content-between mt-2 pt-2 border-top" style={{ fontSize: "11.5px" }}>
                  <span className="text-muted">Customer returns</span>
                  <Link
                    to={refundAccount ? `/admin/ledger?accountId=${refundAccount.id}` : "/admin/ledger"}
                    className="text-primary fw-semibold text-decoration-none d-flex align-items-center gap-1"
                  >
                    Ledger <FaArrowRight size={10} />
                  </Link>
                </div>
              </div>
            </div>

            {/* 3. Courier Account */}
            <div className="col-12 col-sm-6 col-lg-4 col-xl-2">
              <div className="admin-stat-card h-100 shadow-sm border-0" style={{ borderLeft: "4px solid #2563eb" }}>
                <div className="admin-stat-header">
                  <div>
                    <span className="badge bg-primary bg-opacity-10 text-primary fw-bold font-monospace px-2 py-0 mb-1" style={{ fontSize: "11px" }}>
                      4200
                    </span>
                    <div className="admin-stat-label fw-bold text-dark" style={{ fontSize: "13.5px" }}>Courier Account</div>
                  </div>
                  <div className="admin-stat-icon" style={{ background: "#eff6ff", color: "#2563eb" }}>
                    <FaTruck />
                  </div>
                </div>
                <div className="admin-stat-value text-primary" style={{ fontSize: "20px" }}>
                  {formatMoney(courierAccount?.balance || 0)}
                </div>
                <div className="d-flex align-items-center justify-content-between mt-2 pt-2 border-top" style={{ fontSize: "11.5px" }}>
                  <span className="text-muted">Delivery fees</span>
                  <Link
                    to={courierAccount ? `/admin/ledger?accountId=${courierAccount.id}` : "/admin/ledger"}
                    className="text-primary fw-semibold text-decoration-none d-flex align-items-center gap-1"
                  >
                    Ledger <FaArrowRight size={10} />
                  </Link>
                </div>
              </div>
            </div>

            {/* 4. Discount Account */}
            <div className="col-12 col-sm-6 col-lg-4 col-xl-2">
              <div className="admin-stat-card h-100 shadow-sm border-0" style={{ borderLeft: "4px solid #d97706" }}>
                <div className="admin-stat-header">
                  <div>
                    <span className="badge bg-warning bg-opacity-10 text-warning fw-bold font-monospace px-2 py-0 mb-1" style={{ fontSize: "11px" }}>
                      4300
                    </span>
                    <div className="admin-stat-label fw-bold text-dark" style={{ fontSize: "13.5px" }}>Discount Account</div>
                  </div>
                  <div className="admin-stat-icon" style={{ background: "#fffbeb", color: "#d97706" }}>
                    <FaTag />
                  </div>
                </div>
                <div className="admin-stat-value" style={{ fontSize: "20px", color: "#b45309" }}>
                  {formatMoney(discountAccount?.balance || 0)}
                </div>
                <div className="d-flex align-items-center justify-content-between mt-2 pt-2 border-top" style={{ fontSize: "11.5px" }}>
                  <span className="text-muted">Coupons & promo</span>
                  <Link
                    to={discountAccount ? `/admin/ledger?accountId=${discountAccount.id}` : "/admin/ledger"}
                    className="text-primary fw-semibold text-decoration-none d-flex align-items-center gap-1"
                  >
                    Ledger <FaArrowRight size={10} />
                  </Link>
                </div>
              </div>
            </div>

            {/* 5. Cash & Courier Settlement Account */}
            <div className="col-12 col-sm-6 col-lg-4 col-xl-2">
              <div className="admin-stat-card h-100 shadow-sm border-0" style={{ borderLeft: "4px solid #0d9488" }}>
                <div className="admin-stat-header">
                  <div>
                    <span className="badge bg-info bg-opacity-10 text-info fw-bold font-monospace px-2 py-0 mb-1" style={{ fontSize: "11px" }}>
                      1110
                    </span>
                    <div className="admin-stat-label fw-bold text-dark" style={{ fontSize: "13.5px" }}>Cash & Settlement</div>
                  </div>
                  <div className="admin-stat-icon" style={{ background: "#f0fdfa", color: "#0d9488" }}>
                    <FaWallet />
                  </div>
                </div>
                <div className="admin-stat-value" style={{ fontSize: "20px", color: "#0f766e" }}>
                  {formatMoney(cashAccount?.balance || 0)}
                </div>
                <div className="d-flex align-items-center justify-content-between mt-2 pt-2 border-top" style={{ fontSize: "11.5px" }}>
                  <span className="text-muted">COD & receipts</span>
                  <Link
                    to={cashAccount ? `/admin/ledger?accountId=${cashAccount.id}` : "/admin/ledger"}
                    className="text-primary fw-semibold text-decoration-none d-flex align-items-center gap-1"
                  >
                    Ledger <FaArrowRight size={10} />
                  </Link>
                </div>
              </div>
            </div>

            {/* 6. Total Net Revenue */}
            <div className="col-12 col-sm-6 col-lg-4 col-xl-2">
              <div className="admin-stat-card h-100 shadow-sm border-0" style={{ borderLeft: "4px solid #7c3aed" }}>
                <div className="admin-stat-header">
                  <div>
                    <span className="badge bg-purple bg-opacity-10 fw-bold px-2 py-0 mb-1" style={{ fontSize: "11px", color: "#7c3aed", backgroundColor: "#f5f3ff" }}>
                      Net Formula
                    </span>
                    <div className="admin-stat-label fw-bold text-dark" style={{ fontSize: "13.5px" }}>Net Sales Revenue</div>
                  </div>
                  <div className="admin-stat-icon" style={{ background: "#f5f3ff", color: "#7c3aed" }}>
                    <FaMoneyBillWave />
                  </div>
                </div>
                <div className="admin-stat-value" style={{ fontSize: "20px", color: "#6d28d9" }}>
                  {formatMoney(netTotalRevenue)}
                </div>
                <div className="d-flex align-items-center justify-content-between mt-2 pt-2 border-top" style={{ fontSize: "11.5px" }}>
                  <span className="text-muted" title="Product Sales + Courier Revenue minus Customer Refunds and Discounts (Excludes Cancelled Orders)">
                    Sale+Courier−Ref−Disc
                  </span>
                  <Link
                    to="/admin/ledger"
                    className="text-primary fw-semibold text-decoration-none d-flex align-items-center gap-1"
                  >
                    Ledger <FaArrowRight size={10} />
                  </Link>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Total Assets */}
            <div className="col-12 col-sm-6 col-xl-3">
              <div className="admin-stat-card h-100">
                <div className="admin-stat-header">
                  <span className="admin-stat-label">Total Assets (Cash & Stock)</span>
                  <div className="admin-stat-icon" style={{ background: "#eff6ff", color: "#2563eb" }}>
                    <FaWallet />
                  </div>
                </div>
                <div className="admin-stat-value" style={{ fontSize: "22px" }}>
                  {formatMoney(summaryTotals.ASSET)}
                </div>
                <div className="admin-stat-footer text-muted" style={{ fontSize: "12px" }}>
                  Bank, Cash on Hand & Inventory
                </div>
              </div>
            </div>

            {/* Total Income */}
            <div className="col-12 col-sm-6 col-xl-3">
              <div className="admin-stat-card h-100">
                <div className="admin-stat-header">
                  <span className="admin-stat-label">Total Revenue (Income)</span>
                  <div className="admin-stat-icon" style={{ background: "#ecfdf5", color: "#16a34a" }}>
                    <FaMoneyBillWave />
                  </div>
                </div>
                <div className="admin-stat-value text-success" style={{ fontSize: "22px" }}>
                  {formatMoney(summaryTotals.REVENUE)}
                </div>
                <div className="admin-stat-footer text-muted" style={{ fontSize: "12px" }}>
                  Total retail sales & delivery income
                </div>
              </div>
            </div>

            {/* Total Expenses */}
            <div className="col-12 col-sm-6 col-xl-3">
              <div className="admin-stat-card h-100">
                <div className="admin-stat-header">
                  <span className="admin-stat-label">Total Expenses</span>
                  <div className="admin-stat-icon" style={{ background: "#fffbeb", color: "#d97706" }}>
                    <FaReceipt />
                  </div>
                </div>
                <div className="admin-stat-value text-warning" style={{ fontSize: "22px" }}>
                  {formatMoney(summaryTotals.EXPENSE)}
                </div>
                <div className="admin-stat-footer text-muted" style={{ fontSize: "12px" }}>
                  COGS, courier costs & gateway fees
                </div>
              </div>
            </div>

            {/* Total Liabilities */}
            <div className="col-12 col-sm-6 col-xl-3">
              <div className="admin-stat-card h-100">
                <div className="admin-stat-header">
                  <span className="admin-stat-label">Total Liabilities (Owed)</span>
                  <div className="admin-stat-icon" style={{ background: "#fff1f2", color: "#e11d48" }}>
                    <FaFileInvoiceDollar />
                  </div>
                </div>
                <div className="admin-stat-value text-danger" style={{ fontSize: "22px" }}>
                  {formatMoney(summaryTotals.LIABILITY)}
                </div>
                <div className="admin-stat-footer text-muted" style={{ fontSize: "12px" }}>
                  Sales taxes and vendor payables
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Search, Category Filters & View Toggle */}
      <div className="admin-card mb-4 p-3 bg-white">
        <div className="row g-3 align-items-center">
          {/* Search Bar */}
          <div className="col-12 col-lg-4">
            <div className="input-group">
              <span className="input-group-text bg-light border-end-0 text-muted">
                <FaSearch size={13} />
              </span>
              <input
                type="text"
                placeholder="Search account name, code, or purpose..."
                className="form-control border-start-0 bg-light"
                style={{ fontSize: "13px" }}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="btn btn-light border border-start-0 text-muted"
                  onClick={() => setSearchQuery("")}
                  style={{ fontSize: "12px" }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Filter Pills */}
          <div className="col-12 col-lg-6 d-flex flex-wrap gap-2">
            <button
              type="button"
              className={`btn btn-sm rounded-pill px-3 fw-semibold ${
                filterType === "ALL" ? "btn-primary" : "btn-light border text-secondary"
              }`}
              style={{ fontSize: "12px" }}
              onClick={() => setFilterType("ALL")}
            >
              All {simpleMode ? "Simple" : ""} Accounts ({filteredAccounts.length})
            </button>
            {(simpleMode
              ? ["REVENUE", "ASSET"]
              : Object.keys(CATEGORIES)
            ).map((key) => {
              const count = accounts.filter(
                (a) =>
                  a.account_type === key &&
                  (!simpleMode || SIMPLE_ACCOUNT_CODES.includes(a.account_code))
              ).length;
              if (count === 0 && simpleMode) return null;
              const cat = CATEGORIES[key];
              return (
                <button
                  key={key}
                  type="button"
                  className={`btn btn-sm rounded-pill px-3 fw-semibold ${
                    filterType === key ? "btn-dark text-white" : "btn-light border text-secondary"
                  }`}
                  style={{ fontSize: "12px" }}
                  onClick={() => setFilterType(key)}
                >
                  {cat.label} ({count})
                </button>
              );
            })}
          </div>

          {/* View Mode Toggle */}
          <div className="col-12 col-lg-2 d-flex justify-content-lg-end">
            <div className="btn-group btn-group-sm rounded-pill p-1 bg-light border">
              <button
                type="button"
                className={`btn btn-sm rounded-pill px-2 py-1 ${
                  viewMode === "grouped" ? "btn-white shadow-sm fw-bold" : "text-muted border-0 bg-transparent"
                }`}
                style={{ fontSize: "12px" }}
                onClick={() => setViewMode("grouped")}
                title="Grouped by Category"
              >
                <FaThLarge size={11} className="me-1" /> Groups
              </button>
              <button
                type="button"
                className={`btn btn-sm rounded-pill px-2 py-1 ${
                  viewMode === "table" ? "btn-white shadow-sm fw-bold" : "text-muted border-0 bg-transparent"
                }`}
                style={{ fontSize: "12px" }}
                onClick={() => setViewMode("table")}
                title="Table View"
              >
                <FaList size={11} className="me-1" /> Table
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Account Content */}
      {loading ? (
        <div className="admin-card py-5 text-center bg-white">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="text-muted mt-2 fw-semibold">Loading accounts...</p>
        </div>
      ) : filteredAccounts.length === 0 ? (
        <div className="admin-card p-5 text-center bg-white">
          <FaBook size={36} className="text-muted mb-2 opacity-50" />
          <h6 className="fw-bold">No accounts found</h6>
          <p className="text-muted mb-3" style={{ fontSize: "13px" }}>
            {searchQuery
              ? `No accounts matched "${searchQuery}".`
              : "No accounts exist for this category."}
          </p>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary rounded-pill px-3"
            onClick={() => {
              setSearchQuery("");
              setFilterType("ALL");
            }}
          >
            Reset Filters
          </button>
        </div>
      ) : viewMode === "grouped" ? (
        /* GROUPED CATEGORY VIEW */
        <div className="d-flex flex-column gap-4">
          {groupedData.map(({ category, items, subtotal }) => {
            const Icon = category.icon;
            return (
              <div key={category.key} className="admin-card mb-0 shadow-sm overflow-hidden bg-white">
                {/* Category Header */}
                <div
                  className="p-3 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2"
                  style={{ backgroundColor: category.bgColor }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="rounded-3 p-2 d-flex align-items-center justify-content-center text-white"
                      style={{ backgroundColor: category.color, width: "36px", height: "36px" }}
                    >
                      <Icon size={16} />
                    </div>
                    <div>
                      <h6 className="fw-bold mb-0" style={{ color: "var(--admin-text-main)" }}>
                        {category.simpleTitle}
                      </h6>
                      <small className="text-muted" style={{ fontSize: "12px" }}>
                        {category.description}
                      </small>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <div className="text-end">
                      <small className="text-muted d-block" style={{ fontSize: "11px", textTransform: "uppercase" }}>
                        Subtotal ({items.length} accounts)
                      </small>
                      <span className="fw-bold" style={{ fontSize: "15px", color: category.color }}>
                        {formatMoney(subtotal)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleOpenModal(category.key)}
                      className="btn btn-sm btn-light border rounded-pill px-2 py-1 d-flex align-items-center gap-1 text-muted"
                      style={{ fontSize: "11px" }}
                      title={`Add new ${category.label}`}
                    >
                      <FaPlus size={9} /> Add
                    </button>
                  </div>
                </div>

                {/* Accounts List within this category */}
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <tbody style={{ fontSize: "13.5px" }}>
                      {items.map((acc) => (
                        <tr key={acc.id}>
                          {/* Code */}
                          <td style={{ width: "90px" }} className="ps-4">
                            <span
                              className="badge bg-light text-dark border font-monospace px-2 py-1"
                              style={{ fontSize: "11px" }}
                            >
                              {acc.account_code}
                            </span>
                          </td>

                          {/* Name & Description */}
                          <td>
                            <div className="d-flex align-items-center flex-wrap gap-2">
                              <span className="fw-bold text-dark">{acc.account_name}</span>
                              {acc.account_code === "4100" && (
                                <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25" style={{ fontSize: "10.5px" }}>
                                  Sale Account
                                </span>
                              )}
                              {acc.account_code === "4400" && (
                                <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25" style={{ fontSize: "10.5px" }}>
                                  Refund Account
                                </span>
                              )}
                              {acc.account_code === "4200" && (
                                <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25" style={{ fontSize: "10.5px" }}>
                                  Courier Account
                                </span>
                              )}
                              {acc.account_code === "4300" && (
                                <span className="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25" style={{ fontSize: "10.5px" }}>
                                  Discount Account
                                </span>
                              )}
                              {acc.account_code === "1110" && (
                                <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25" style={{ fontSize: "10.5px" }}>
                                  Cash & Settlement
                                </span>
                              )}
                            </div>
                            {acc.description && (
                              <small className="text-muted d-block mt-0.5" style={{ fontSize: "12px" }}>
                                {acc.description}
                              </small>
                            )}
                          </td>

                          {/* Balance */}
                          <td className="text-end pe-4" style={{ width: "200px" }}>
                            <div className="fw-bold" style={{ fontSize: "14px" }}>
                              <span className={Number(acc.balance) < 0 ? "text-danger" : "text-dark"}>
                                {formatMoney(acc.balance)}
                              </span>
                            </div>
                            <small className="text-muted" style={{ fontSize: "11px" }}>
                              Current Running Balance
                            </small>
                          </td>

                          {/* Quick Action */}
                          <td className="text-center pe-4" style={{ width: "120px" }}>
                            <Link
                              to={`/admin/ledger?accountId=${acc.id}`}
                              className="btn btn-sm btn-outline-secondary rounded-pill px-3 py-1 d-inline-flex align-items-center gap-1"
                              style={{ fontSize: "12px" }}
                            >
                              Ledger <FaArrowRight size={10} />
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* SIMPLE TABLE VIEW */
        <div className="admin-card overflow-hidden bg-white">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.04em" }}>
                <tr>
                  <th className="ps-4" style={{ width: "100px" }}>Code</th>
                  <th>Account Name & Purpose</th>
                  <th>Category</th>
                  <th className="text-end">Current Balance</th>
                  <th className="text-center pe-4" style={{ width: "120px" }}>Action</th>
                </tr>
              </thead>
              <tbody style={{ fontSize: "13.5px" }}>
                {filteredAccounts.map((acc) => {
                  const cat = CATEGORIES[acc.account_type] || {};
                  return (
                    <tr key={acc.id}>
                      <td className="ps-4">
                        <span className="badge bg-light text-dark border font-monospace px-2 py-1" style={{ fontSize: "11.5px" }}>
                          {acc.account_code}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center flex-wrap gap-2">
                          <span className="fw-bold text-dark">{acc.account_name}</span>
                          {acc.account_code === "4100" && (
                            <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25" style={{ fontSize: "10.5px" }}>
                              Sale Account
                            </span>
                          )}
                          {acc.account_code === "4400" && (
                            <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25" style={{ fontSize: "10.5px" }}>
                              Refund Account
                            </span>
                          )}
                          {acc.account_code === "4200" && (
                            <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25" style={{ fontSize: "10.5px" }}>
                              Courier Account
                            </span>
                          )}
                          {acc.account_code === "4300" && (
                            <span className="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25" style={{ fontSize: "10.5px" }}>
                              Discount Account
                            </span>
                          )}
                          {acc.account_code === "1110" && (
                            <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25" style={{ fontSize: "10.5px" }}>
                              Cash & Settlement
                            </span>
                          )}
                        </div>
                        {acc.description && (
                          <small className="text-muted d-block mt-0.5" style={{ fontSize: "12px" }}>
                            {acc.description}
                          </small>
                        )}
                      </td>
                      <td>
                        <span className={`badge rounded-pill px-2 py-1 ${cat.badgeClass || "bg-secondary text-white"}`} style={{ fontSize: "11px" }}>
                          {cat.label || acc.account_type}
                        </span>
                      </td>
                      <td className="text-end fw-bold">
                        <span className={Number(acc.balance) < 0 ? "text-danger" : "text-dark"}>
                          {formatMoney(acc.balance)}
                        </span>
                      </td>
                      <td className="text-center pe-4">
                        <Link
                          to={`/admin/ledger?accountId=${acc.id}`}
                          className="btn btn-sm btn-outline-primary rounded-pill px-3 py-1 d-inline-flex align-items-center gap-1"
                          style={{ fontSize: "11.5px" }}
                        >
                          Ledger <FaArrowRight size={9} />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Simplified "Add Account" Modal */}
      {openModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(15, 23, 42, 0.6)", zIndex: 1050 }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
              {/* Modal Header */}
              <div className="modal-header border-bottom px-4 py-3 bg-light">
                <div>
                  <h5 className="modal-title fw-bold mb-0" style={{ fontSize: "16px" }}>
                    Add New Account
                  </h5>
                  <small className="text-muted" style={{ fontSize: "12px" }}>
                    Create a new bookkeeping account to track money, sales, or expenses
                  </small>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setOpenModal(false)}
                ></button>
              </div>

              <form onSubmit={handleSaveAccount}>
                <div className="modal-body p-4">
                  {/* Step 1: Category Selection Cards */}
                  <div className="mb-4">
                    <label className="form-label fw-bold mb-2" style={{ fontSize: "13px" }}>
                      1. Choose Account Category
                    </label>
                    <div className="row g-2">
                      {Object.keys(CATEGORIES).map((key) => {
                        const cat = CATEGORIES[key];
                        const isSelected = formData.account_type === key;
                        const CatIcon = cat.icon;
                        return (
                          <div key={key} className="col-12 col-md-6 col-lg-4">
                            <div
                              onClick={() => handleTypeSelectInModal(key)}
                              className={`p-3 rounded-3 border cursor-pointer h-100 transition-all ${
                                isSelected ? "border-primary bg-primary bg-opacity-10 shadow-sm" : "bg-white"
                              }`}
                              style={{
                                cursor: "pointer",
                                borderColor: isSelected ? "var(--admin-primary)" : "#e2e8f0",
                                borderWidth: isSelected ? "2px" : "1px",
                              }}
                            >
                              <div className="d-flex align-items-center justify-content-between mb-1">
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    className="rounded-2 p-1 text-white d-flex align-items-center justify-content-center"
                                    style={{ backgroundColor: cat.color, width: "24px", height: "24px", fontSize: "11px" }}
                                  >
                                    <CatIcon />
                                  </div>
                                  <span className="fw-bold" style={{ fontSize: "13px", color: isSelected ? "var(--admin-primary)" : "#1e293b" }}>
                                    {cat.label}
                                  </span>
                                </div>
                                {isSelected && <FaCheck size={12} className="text-primary" />}
                              </div>
                              <small className="text-muted d-block" style={{ fontSize: "11px", lineHeight: "1.3" }}>
                                {key === "ASSET" && "Cash, Bank, Inventory"}
                                {key === "REVENUE" && "Sales, Shipping Income"}
                                {key === "EXPENSE" && "Couriers, Ads, Bills"}
                                {key === "LIABILITY" && "Taxes, Supplier debts"}
                                {key === "EQUITY" && "Capital, Retained profit"}
                              </small>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Step 2: Account Name & Auto Code */}
                  <div className="row g-3 mb-3">
                    <div className="col-12 col-md-8">
                      <label className="form-label fw-bold" style={{ fontSize: "13px" }}>
                        2. Account Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Meezan Bank Account, Courier Charges, Packaging..."
                        className="form-control"
                        style={{ fontSize: "13.5px" }}
                        value={formData.account_name}
                        onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                      />
                      <small className="text-muted" style={{ fontSize: "11.5px" }}>
                        Give this account a clear, human-friendly title.
                      </small>
                    </div>

                    <div className="col-12 col-md-4">
                      <label className="form-label fw-bold d-flex align-items-center justify-content-between" style={{ fontSize: "13px" }}>
                        <span>Account Code</span>
                        <span className="badge bg-light text-muted border" style={{ fontSize: "10px" }}>Auto-assigned</span>
                      </label>
                      <input
                        type="text"
                        required
                        className="form-control font-monospace fw-bold"
                        style={{ fontSize: "13.5px" }}
                        value={formData.account_code}
                        onChange={(e) => setFormData({ ...formData, account_code: e.target.value })}
                      />
                      <small className="text-muted" style={{ fontSize: "11.5px" }}>
                        Bookkeeping number code.
                      </small>
                    </div>
                  </div>

                  {/* Step 3: Description (Optional) */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                      Description & Notes <span className="text-muted fw-normal">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      style={{ fontSize: "13px" }}
                      placeholder="Brief note on what transactions are recorded in this account..."
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>

                  {/* Advanced Settings Toggle */}
                  <div className="border-top pt-3">
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-decoration-none text-muted p-0 d-flex align-items-center gap-1"
                      style={{ fontSize: "12px" }}
                      onClick={() => setShowAdvancedInModal(!showAdvancedInModal)}
                    >
                      {showAdvancedInModal ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                      {showAdvancedInModal ? "Hide Advanced Options" : "Show Advanced Options (for Accountants)"}
                    </button>

                    {showAdvancedInModal && (
                      <div className="row g-3 mt-1 p-3 bg-light rounded-3">
                        <div className="col-12 col-md-6">
                          <label className="form-label fw-semibold" style={{ fontSize: "12.5px" }}>
                            Parent Account
                          </label>
                          <select
                            className="form-select form-select-sm"
                            value={formData.parent_id}
                            onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                          >
                            <option value="">None (Top-Level Account)</option>
                            {accounts
                              .filter((a) => !a.parent_id && a.account_type === formData.account_type)
                              .map((a) => (
                                <option key={a.id} value={a.id}>
                                  {a.account_code} — {a.account_name}
                                </option>
                              ))}
                          </select>
                        </div>
                        <div className="col-12 col-md-6">
                          <label className="form-label fw-semibold" style={{ fontSize: "12.5px" }}>
                            Normal Accounting Balance
                          </label>
                          <select
                            className="form-select form-select-sm"
                            value={formData.normal_balance}
                            onChange={(e) => setFormData({ ...formData, normal_balance: e.target.value })}
                          >
                            <option value="DEBIT">DEBIT (Standard for Assets & Expenses)</option>
                            <option value="CREDIT">CREDIT (Standard for Revenue & Liabilities)</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="modal-footer border-top bg-light px-4 py-3">
                  <button
                    type="button"
                    className="btn btn-sm btn-light border rounded-pill px-3"
                    onClick={() => setOpenModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="btn btn-sm btn-primary rounded-pill fw-semibold px-4"
                    style={{ background: "var(--admin-primary)", border: "none" }}
                  >
                    {saving ? "Saving..." : "Create Account"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
